import { defineComponent as _defineComponent } from 'vue'
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';
import { OfferLocation } from '@/helpers/offer-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';

import { computed, ref } from 'vue';
import { MonetizationType, ObjectType } from '@/@types/graphql-types';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { isTitleSeason } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { getDisneyImpressionUrl, useProviderPromotion } from '@/helpers/provider-promotion';
import DisclaimerButton from '@/components/buybox-new/DisclaimerButton.vue';
import ImpressionPixel from '@/components/sponsored-recommendations/ImpressionPixel.vue';
import { useAmzAssociatesImpactExp } from './experiments/AmazonAssoicatesImpactExp';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProviderPromotionOffer',
  props: {
    title: null,
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { title: TitleDetail, additionalContexts: SnowplowContext[] };



/** impression tracking for Disney+ */
const mountImpressionPixel = ref(false);

const { country, webLocale } = useLanguageStore();
const { xServeTrack } = useAppStore();
const { setAmzAssImpExpTag } = useAmzAssociatesImpactExp();

const { providerPromotion: promotion } = useProviderPromotion(() => props.title);

// we can be reasonably sure there is an offer because the parent would check
// nonetheless, there's an extra check in the template
const offer = computed(() =>
	props.title.offers.find(
		offer =>
			[MonetizationType.Flatrate, MonetizationType.Free].includes(offer.monetizationType) &&
			offer.package.packageId === promotion.value?.provider
	)
);

const disneyPlusImpressionPixel = computed(() => {
	return promotion.value?.provider === 337 ? getDisneyImpressionUrl(337, webLocale.value) : null;
});

const showDisclaimerButton = computed(() => promotion.value?.disclaimerText != null);

const titleObjectId = computed(() => (isTitleSeason(props.title) ? props.title.show?.objectId : props.title.objectId));
const titleObjectType = computed(() => (isTitleSeason(props.title) ? ObjectType.Show : props.title.objectType));

const titleContext = computed(() => new SnowplowTitleContextGraphql(titleObjectId.value, titleObjectType.value));

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const clickOutUrl = computed(() => {
	const clickoutOffer = promotion.value?.url ? { ...offer.value, standardWebURL: promotion.value.url } : offer.value;
	const skipRedirect = !!promotion.value?.url;
	const isATVPlus = offer.value?.package?.packageId === 350;

	const url = new ClickoutUrl(clickoutOffer)
		.setTitleSourceId(props.title.id)
		.skipYieldOptimization(isATVPlus)
		.skipRedirect(skipRedirect)
		.addContexts(titleContext.value)
		.set('uct_country', country.value)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'promoted')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'offerEntityId', props.title.id)
		.setCanary(xServeTrack.value);

	if (isATVPlus) {
		url.set('uct_ct', 'applepromo');
	}

	if (['dnp', 'bng', 'hst'].includes(offer.value?.package?.shortName || '')) {
		url.skipUCT2();
		url.set('uct_ct', 'backdrop-promo');
	}

	return setAmzAssImpExpTag(url);
});

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible) return;

	onIntersection(clickOutUrl.value, isVisible);

	if (!mountImpressionPixel.value) {
		mountImpressionPixel.value = true;
	}
}

function handleClickout() {
	if (offer.value == null) return;
}

return { __sfc: true,props, mountImpressionPixel, country, webLocale, xServeTrack, setAmzAssImpExpTag, promotion, offer, disneyPlusImpressionPixel, showDisclaimerButton, titleObjectId, titleObjectType, titleContext, onIntersection, clickOutUrl, observeVisibilityOptions, onVisibilityChanged, handleClickout, ProviderIcon, DisclaimerButton, ImpressionPixel }
}

})